/**
 * Currency formatting helpers used site-wide.
 *
 * The platform's primary currency is PHP (Philippine peso). For member
 * transparency we also show the USD equivalent next to every price, e.g.
 *   ₱4,500 ($76.26)
 *
 * Rates come from `useFxRate()` (see context/FxRateContext.js) and update
 * once per page-load, cached server-side for 1 hour.
 */

const PHP_LOCALE = 'en-PH';
const USD_LOCALE = 'en-US';

function safeNumber(v) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/** Format PHP amount with peso sign and locale grouping. */
export function formatPHP(amount, opts = {}) {
  const n = safeNumber(amount);
  const minDigits = opts.maxFractionDigits ?? (Math.abs(n) % 1 === 0 ? 0 : 2);
  return `₱${n.toLocaleString(PHP_LOCALE, {
    minimumFractionDigits: opts.minFractionDigits ?? minDigits,
    maximumFractionDigits: opts.maxFractionDigits ?? minDigits,
  })}`;
}

/** Format USD amount with dollar sign. */
export function formatUSD(amount, opts = {}) {
  const n = safeNumber(amount);
  return `$${n.toLocaleString(USD_LOCALE, {
    minimumFractionDigits: opts.minFractionDigits ?? 2,
    maximumFractionDigits: opts.maxFractionDigits ?? 2,
  })}`;
}

/**
 * Convert a PHP amount to USD using a php→usd rate.
 *  - phpUsdRate: USD per 1 PHP (e.g. 0.01786). If 0/missing, returns null.
 */
export function phpToUsd(amountPhp, phpUsdRate) {
  const n = safeNumber(amountPhp);
  if (!phpUsdRate || phpUsdRate <= 0) return null;
  return n * phpUsdRate;
}

/** Convert a USD amount to PHP using either php→usd or usd→php rate. */
export function usdToPhp(amountUsd, { phpUsdRate, usdPhpRate } = {}) {
  const n = safeNumber(amountUsd);
  if (usdPhpRate && usdPhpRate > 0) return n * usdPhpRate;
  if (phpUsdRate && phpUsdRate > 0) return n / phpUsdRate;
  return null;
}

/**
 * Site-wide dual-currency formatter for prices listed in PHP.
 *
 * Returns "₱4,500 ($76.26)" or just "₱4,500" if the rate is unavailable.
 *
 * Options:
 *   showUsd      — pass false to suppress the USD half (default true)
 *   phpUsdRate   — required to render the USD half
 *   compact      — when true, drops the parentheses ("₱4,500 · $76.26")
 *   phpDecimals  — control decimal places on PHP (auto by default)
 */
export function formatDualPHP(amountPhp, {
  phpUsdRate,
  showUsd = true,
  compact = false,
  phpDecimals,
} = {}) {
  const php = formatPHP(amountPhp, {
    minFractionDigits: phpDecimals,
    maxFractionDigits: phpDecimals,
  });
  if (!showUsd) return php;
  const usdAmt = phpToUsd(amountPhp, phpUsdRate);
  if (usdAmt == null) return php;
  const usd = formatUSD(usdAmt);
  return compact ? `${php} · ${usd}` : `${php} (${usd})`;
}

/**
 * Site-wide dual-currency formatter for prices listed in USD
 * (e.g. stock certificates). Returns "$76.26 (₱4,500)".
 */
export function formatDualUSD(amountUsd, {
  phpUsdRate,
  usdPhpRate,
  showPhp = true,
  compact = false,
} = {}) {
  const usd = formatUSD(amountUsd);
  if (!showPhp) return usd;
  const phpAmt = usdToPhp(amountUsd, { phpUsdRate, usdPhpRate });
  if (phpAmt == null) return usd;
  const php = formatPHP(phpAmt, { minFractionDigits: 0, maxFractionDigits: 0 });
  return compact ? `${usd} · ${php}` : `${usd} (${php})`;
}
